import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { Today } from './pages/Today';
import { Week } from './pages/Week';
import { NewBooking } from './pages/NewBooking';
import { Customers } from './pages/Customers';
import { CustomerDetail } from './pages/CustomerDetail';
import { Settings } from './pages/Settings';
import { SettingsServices } from './pages/SettingsServices';
import { SettingsHours } from './pages/SettingsHours';
import { SettingsCalendar } from './pages/SettingsCalendar';
import { SettingsEmails } from './pages/SettingsEmails';
import { isAuthed } from './lib/auth';
import { TenantProvider } from './lib/tenantContext';

// Tiny page wrapper — 150ms fade + 4px slide-up on mount, mirror on exit.
// AnimatePresence in <App/> swaps these on route change so the page content
// area gets a subtle transition without animating the nav/header.
function PageWrapper({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.15, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
}

export function App() {
  const [authed, setAuthed] = useState(isAuthed());
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    function onUnauth() {
      setAuthed(false);
      navigate('/login', { replace: true });
    }
    window.addEventListener('ngb:unauthorized', onUnauth);
    return () => window.removeEventListener('ngb:unauthorized', onUnauth);
  }, [navigate]);

  useEffect(() => {
    setAuthed(isAuthed());
  }, [location]);

  if (!authed) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <TenantProvider>
      <Layout>
        <AnimatePresence mode="wait" initial={false}>
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<PageWrapper><Today /></PageWrapper>} />
            <Route path="/week" element={<PageWrapper><Week /></PageWrapper>} />
            <Route path="/new-booking" element={<PageWrapper><NewBooking /></PageWrapper>} />
            <Route path="/customers" element={<PageWrapper><Customers /></PageWrapper>} />
            <Route path="/customers/:id" element={<PageWrapper><CustomerDetail /></PageWrapper>} />
            <Route path="/settings" element={<PageWrapper><Settings /></PageWrapper>} />
            <Route path="/settings/services" element={<PageWrapper><SettingsServices /></PageWrapper>} />
            <Route path="/settings/hours" element={<PageWrapper><SettingsHours /></PageWrapper>} />
            <Route path="/settings/email-templates" element={<PageWrapper><SettingsEmails /></PageWrapper>} />
            <Route path="/settings/calendar" element={<PageWrapper><SettingsCalendar /></PageWrapper>} />
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AnimatePresence>
      </Layout>
    </TenantProvider>
  );
}
